import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getDiagnosticProgress, getDiagnosticFullReport,
  getExamDates, createExamDate, updateExamDate, deleteExamDate,
  getPastResults, createPastResult, uploadPastResults, deletePastResult,
  getStudyPlan, generateStudyPlan, refreshPredictions,
  getScaffoldingStatus,
} from '../api/endpoints';
import {
  ClipboardList, CheckCircle, Lock, ChevronRight,
  Brain, TrendingUp, Target, Zap, Clock, AlertCircle, Stethoscope,
  Award, AlertTriangle, ArrowUp, ArrowDown, Shield, Eye, Timer, BookOpen,
  Calendar, FileText, Plus, Trash2, Edit3, Upload, Loader2, ChevronDown
} from 'lucide-react';

const DiagnosticPage = () => {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Exam dates & past results state
  const [examDates, setExamDates] = useState([]);
  const [pastResults, setPastResults] = useState([]);
  const [studyPlan, setStudyPlan] = useState(null);
  const [scaffoldingStatus, setScaffoldingStatus] = useState(null);
  const [showExamForm, setShowExamForm] = useState(false);
  const [showResultForm, setShowResultForm] = useState(false);
  const [examFormData, setExamFormData] = useState({ exam_name: '', exam_date: '', total_questions: '', category_weights: [], notes: '' });
  const [resultFormData, setResultFormData] = useState({ exam_name: '', exam_date: '', overall_score_pct: '', category_results: [], subjects: [], notes: '' });
  const [savingExam, setSavingExam] = useState(false);
  const [savingResult, setSavingResult] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [expandedPlanWeeks, setExpandedPlanWeeks] = useState({});

  useEffect(() => {
    document.title = `Diagnostic Assessment | ${tenant?.name || 'MedMCQ'}`;
  }, [tenant]);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const res = await getDiagnosticProgress();
      setProgress(res.data);
      // Load full report and supplementary data if all tests are complete
      if (res.data?.all_complete) {
        try {
          const reportRes = await getDiagnosticFullReport();
          if (reportRes.data?.has_data) setReport(reportRes.data);
        } catch (e) {
          console.error('Could not load diagnostic report:', e);
        }
        // Load exam dates, past results, study plan, and scaffolding status in parallel
        loadExamDates();
        loadPastResults();
        loadStudyPlan();
        loadScaffoldingStatus();
      }
    } catch (e) {
      setError('Could not load your progress. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadExamDates = async () => {
    try {
      const res = await getExamDates();
      setExamDates(res.data?.exam_dates || []);
    } catch (e) { /* tier-gated, silently ignore */ }
  };

  const loadPastResults = async () => {
    try {
      const res = await getPastResults();
      setPastResults(res.data?.past_results || []);
    } catch (e) { /* tier-gated, silently ignore */ }
  };

  const loadStudyPlan = async () => {
    try {
      const res = await getStudyPlan();
      setStudyPlan(res.data?.study_plan || null);
    } catch (e) { /* tier-gated, silently ignore */ }
  };

  const loadScaffoldingStatus = async () => {
    try {
      const res = await getScaffoldingStatus();
      setScaffoldingStatus(res.data || null);
    } catch (e) { /* Elite-only, silently ignore for other tiers */ }
  };

  const handleSaveExamDate = async () => {
    if (!examFormData.exam_name || !examFormData.exam_date) return;
    setSavingExam(true);
    try {
      await createExamDate({
        ...examFormData,
        total_questions: examFormData.total_questions ? parseInt(examFormData.total_questions) : 0,
      });
      setShowExamForm(false);
      setExamFormData({ exam_name: '', exam_date: '', total_questions: '', category_weights: [], notes: '' });
      loadExamDates();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save exam date');
    } finally {
      setSavingExam(false);
    }
  };

  const handleDeleteExamDate = async (id) => {
    try {
      await deleteExamDate(id);
      loadExamDates();
    } catch (e) {
      alert('Failed to delete');
    }
  };

  const handleSavePastResult = async () => {
    if (!resultFormData.exam_name || !resultFormData.exam_date || !resultFormData.overall_score_pct) return;
    setSavingResult(true);
    try {
      await createPastResult({
        ...resultFormData,
        overall_score_pct: parseFloat(resultFormData.overall_score_pct),
      });
      setShowResultForm(false);
      setResultFormData({ exam_name: '', exam_date: '', overall_score_pct: '', category_results: [], subjects: [], notes: '' });
      loadPastResults();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save result');
    } finally {
      setSavingResult(false);
    }
  };

  const handleDeletePastResult = async (id) => {
    try {
      await deletePastResult(id);
      loadPastResults();
    } catch (e) {
      alert('Failed to delete');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    setUploadPreview(null);
    try {
      const res = await uploadPastResults(file);
      setUploadPreview(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to parse file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleConfirmUpload = async (parsed) => {
    try {
      await createPastResult({
        exam_name: parsed.exam_name || 'Uploaded Result',
        exam_date: parsed.exam_date || new Date().toISOString().slice(0, 10),
        overall_score_pct: parsed.overall_score_pct || 0,
        category_results: parsed.category_results || [],
        subjects: parsed.subjects || [],
        source: 'uploaded',
      });
      setUploadPreview(null);
      loadPastResults();
    } catch (e) {
      alert('Failed to save uploaded result');
    }
  };

  const handleGenerateStudyPlan = async () => {
    setGeneratingPlan(true);
    try {
      const res = await generateStudyPlan();
      setStudyPlan(res.data?.study_plan || null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate study plan');
    } finally {
      setGeneratingPlan(false);
    }
  };

  // Available categories for multi-select
  const CATEGORIES = [
    'anatomy', 'biochemistry', 'cardiology', 'dermatology', 'endocrinology',
    'gastroenterology', 'hematology', 'immunology', 'infectious disease',
    'medicine', 'microbiology', 'nephrology', 'neurology', 'neuroscience',
    'obstetrics', 'ophthalmology', 'orthopedics', 'pathology', 'pediatrics',
    'pharmacology', 'physiology', 'psychiatry', 'radiology', 'renal',
    'respiratory', 'rheumatology', 'surgery', 'urology',
  ];

  const getTestStatus = (testNum) => {
    if (!progress) return 'locked';
    if (progress.tests_completed.includes(testNum)) return 'complete';
    if (testNum === progress.current_test) return 'available';
    if (testNum < progress.current_test) return 'complete';
    return 'locked';
  };

  const getTestActionLabel = (testNum) => {
    const status = getTestStatus(testNum);
    if (status === 'complete') return 'Completed';
    if (status === 'locked') return 'Locked';
    const answered = testNum === progress?.current_test
      ? (progress?.questions_answered_in_current_test || 0)
      : 0;
    if (answered > 0) return 'Continue';
    return 'Start Test';
  };

  const handleStartTest = (testNum) => {
    const status = getTestStatus(testNum);
    if (status === 'complete' || status === 'locked') return;
    navigate(`/diagnostic/${testNum}`);
  };

  const tests = [
    {
      num: 1,
      title: 'Diagnostic Test 1',
      subtitle: 'Foundation Assessment',
      description: 'Covers anatomy, physiology, biochemistry and foundational sciences across all year levels.',
      icon: Brain,
      color: 'from-cyan-500 to-cyan-600',
    },
    {
      num: 2,
      title: 'Diagnostic Test 2',
      subtitle: 'Clinical Science Assessment',
      description: 'Covers pathology, pharmacology, microbiology, immunology and clinical applications.',
      icon: Target,
      color: 'from-violet-500 to-violet-600',
    },
    {
      num: 3,
      title: 'Diagnostic Test 3',
      subtitle: 'Integrated Assessment',
      description: 'Covers clinical reasoning, applied medicine and integrated multi-system questions.',
      icon: TrendingUp,
      color: 'from-emerald-500 to-emerald-600',
    },
  ];

  // Helpers for the diagnostic report
  const getStrengthLabel = (pct) => {
    if (pct >= 80) return { label: 'Strong', color: 'text-emerald-600', bg: 'bg-emerald-100' };
    if (pct >= 60) return { label: 'Good', color: 'text-cyan-600', bg: 'bg-cyan-100' };
    if (pct >= 40) return { label: 'Developing', color: 'text-amber-600', bg: 'bg-amber-100' };
    return { label: 'Needs Focus', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const getBarColor = (pct) => {
    if (pct >= 80) return 'linear-gradient(90deg, #10b981, #059669)';
    if (pct >= 60) return 'linear-gradient(90deg, #06b6d4, #0891b2)';
    if (pct >= 40) return 'linear-gradient(90deg, #f59e0b, #d97706)';
    return 'linear-gradient(90deg, #ef4444, #dc2626)';
  };

  const categories = report?.category_results || [];
  const sortedByAccuracy = [...categories].sort((a, b) => b.accuracy_pct - a.accuracy_pct);
  const strongAreas = sortedByAccuracy.filter(c => c.accuracy_pct >= 60);
  const weakAreas = sortedByAccuracy.filter(c => c.accuracy_pct < 60);
  const overallPct = report?.overall_accuracy_pct ?? null;
  const totalAnswered = report?.total_answered ?? 0;
  const testResults = report?.test_results || [];
  const confidenceSummary = report?.confidence_summary || {};
  const overconfidentCats = report?.overconfident_categories || [];
  const underconfidentCats = report?.underconfident_categories || [];

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Icon3D icon={ClipboardList} colour="turquoise" size="md" />
            <h1 className="text-2xl font-bold text-slate-900">Diagnostic Assessment</h1>
          </div>
          <p className="text-slate-500 ml-13">3 tests · 110 questions each · ~45 minutes per test</p>
        </div>

        {error && (
          <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Science-backed recommendation card */}
        {!progress?.all_complete && (
          <div
            className="mb-8 rounded-2xl p-6 text-white relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0a192f 0%, #164e63 100%)' }}
          >
            <div className="absolute top-0 right-0 w-48 h-48 opacity-10">
              <Stethoscope className="w-full h-full text-cyan-300" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-cyan-400" />
                <span className="text-cyan-400 font-semibold text-sm uppercase tracking-wide">
                  Highly Recommended
                </span>
              </div>
              <h2 className="text-xl font-bold mb-2">
                Unlock your full personalised learning experience
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                Completing the diagnostic assessment allows MedMCQ to build a detailed profile of your knowledge,
                learning style, and any misconceptions you may hold. Backed by evidence-based
                adaptive learning research — students who complete the diagnostic phase see significantly
                stronger exam performance because every subsequent question is chosen specifically to
                target their weakest areas and build on their strengths.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/10 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-cyan-300">+8.9%</div>
                  <div className="text-xs text-slate-300 mt-1">Average exam score improvement</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-cyan-300">330</div>
                  <div className="text-xs text-slate-300 mt-1">Questions to personalise your engine</div>
                </div>
                <div className="bg-white/10 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-cyan-300">~2.5h</div>
                  <div className="text-xs text-slate-300 mt-1">Total time across 3 tests</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* All complete banner */}
        {progress?.all_complete && (
          <div className="mb-8 rounded-2xl p-6 bg-emerald-50 border border-emerald-200" style={{ boxShadow: '0 8px 24px rgba(16, 185, 129, 0.15), 0 4px 12px rgba(0,0,0,0.08)' }}>
            <div className="flex items-center gap-3">
              <Icon3D icon={CheckCircle} colour="green" size="md" />
              <div>
                <h2 className="font-bold text-emerald-800">Diagnostic Assessment Complete</h2>
                <p className="text-sm text-emerald-600">
                  Your personalised adaptive learning engine is now active. Every question you practice is now chosen specifically for you.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress overview */}
        {progress && (
          <div className="mb-6 bg-white rounded-2xl border border-slate-200 p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-slate-700">Overall progress</span>
              <span className="text-sm text-slate-500">
                {progress.tests_completed.length} of 3 tests complete
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${progress.progress_pct}%`,
                  background: 'linear-gradient(90deg, rgb(6, 182, 212), rgb(8, 145, 178))',
                }}
              />
            </div>
          </div>
        )}

        {/* Test cards */}
        <div className="space-y-4">
          {tests.map((test) => {
            const status = getTestStatus(test.num);
            const actionLabel = getTestActionLabel(test.num);
            const Icon = test.icon;
            const isLocked = status === 'locked';
            const isComplete = status === 'complete';
            const answered = test.num === progress?.current_test
              ? (progress?.questions_answered_in_current_test || 0)
              : 0;

            return (
              <div
                key={test.num}
                className={`bg-white rounded-2xl border transition-all ${
                  isLocked
                    ? 'border-slate-200 opacity-60'
                    : isComplete
                    ? 'border-emerald-200'
                    : 'border-slate-200 hover:border-cyan-300 hover:shadow-md cursor-pointer'
                }`}
                style={{ boxShadow: 'var(--shadow-card)' }}
                onClick={() => !isLocked && !isComplete && handleStartTest(test.num)}
              >
                <div className="p-5 flex items-center gap-4">
                  {/* Icon */}
                  {isComplete ? (
                    <Icon3D icon={CheckCircle} colour="green" size="sm" />
                  ) : isLocked ? (
                    <Icon3D icon={Lock} colour="purple" size="sm" />
                  ) : (
                    <Icon3D icon={Icon} colour={
                      test.num === 1 ? 'turquoise' : test.num === 2 ? 'purple' : 'green'
                    } size="sm" />
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="font-bold text-slate-900">{test.title}</h3>
                      {isComplete && (
                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                          Complete
                        </span>
                      )}
                      {!isComplete && !isLocked && answered > 0 && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                          In Progress
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mb-1">{test.subtitle}</p>
                    <p className="text-xs text-slate-400 hidden sm:block">{test.description}</p>

                    {/* Progress bar for in-progress test */}
                    {!isComplete && !isLocked && answered > 0 && (
                      <div className="mt-2">
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden w-48">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${(answered / 110) * 100}%`,
                              background: 'linear-gradient(90deg, rgb(6, 182, 212), rgb(8, 145, 178))',
                            }}
                          />
                        </div>
                        <span className="text-xs text-slate-400 mt-0.5 block">{answered} / 110 questions</span>
                      </div>
                    )}
                  </div>

                  {/* Meta info */}
                  <div className="hidden sm:flex flex-col items-end gap-1 text-xs text-slate-400 mr-2">
                    <div className="flex items-center gap-1">
                      <ClipboardList className="w-3.5 h-3.5" />
                      <span>110 questions</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span>~45 minutes</span>
                    </div>
                  </div>

                  {/* Action button */}
                  <div className="flex-shrink-0">
                    {isComplete ? (
                      <Icon3D icon={CheckCircle} colour="green" size="sm" />
                    ) : isLocked ? (
                      <Icon3D icon={Lock} colour="purple" size="sm" />
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStartTest(test.num); }}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all hover:opacity-90"
                        style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                      >
                        {actionLabel}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Skip option */}
        {!progress?.all_complete && (
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/questions')}
              className="text-sm text-slate-400 hover:text-slate-600 underline"
            >
              Skip for now — take me to the question bank
            </button>
            <p className="text-xs text-slate-400 mt-1">
              You can always return to the diagnostic assessment from the navigation menu.
            </p>
          </div>
        )}

        {/* Diagnostic Findings Report — shown after all 3 tests complete */}
        {progress?.all_complete && report && categories.length > 0 && (
          <div className="mt-10">
            {/* Report header */}
            <div className="flex items-center gap-3 mb-6">
              <Icon3D icon={Brain} colour="turquoise" size="md" />
              <div>
                <h2 className="text-xl font-bold text-slate-900">Diagnostic Findings Report</h2>
                <p className="text-sm text-slate-500">{totalAnswered} responses analysed across {categories.length} categories · {testResults.length} tests</p>
              </div>
            </div>

            {/* Overall stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="text-3xl font-bold text-cyan-600">{overallPct}%</div>
                <div className="text-sm text-slate-500 mt-1">Overall Accuracy</div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="text-3xl font-bold text-emerald-600">{strongAreas.length}</div>
                <div className="text-sm text-slate-500 mt-1">Strong Categories</div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="text-3xl font-bold text-amber-600">{weakAreas.length}</div>
                <div className="text-sm text-slate-500 mt-1">Areas to Develop</div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-5 text-center" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="text-3xl font-bold text-cyan-600">{report.fast_wrong_count || 0}</div>
                <div className="text-sm text-slate-500 mt-1">Fast Wrong (&lt;5s)</div>
              </div>
            </div>

            {/* What this means */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Icon3D icon={Brain} colour="turquoise" size="sm" />
                <h3 className="font-bold text-slate-900">What This Means</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                Your diagnostic assessment analysed {totalAnswered} responses across {categories.length} medical categories.
                {overallPct >= 70
                  ? ' You\'re performing well overall — your adaptive engine will refine your knowledge in targeted areas while maintaining your strengths.'
                  : overallPct >= 50
                  ? ' You have a solid foundation in several areas, with clear opportunities to strengthen specific categories. Your adaptive engine will focus on closing these gaps.'
                  : ' Your diagnostic has identified several categories where focused practice will make a real difference. This is exactly what the adaptive engine is designed for — building strong foundations one step at a time.'}
              </p>
              <p className="text-sm text-slate-500">
                From now on, every question you practise is selected specifically to target your weaker areas at the right difficulty level, while reinforcing what you already know.
              </p>
            </div>

            {/* Per-test results */}
            {testResults.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Icon3D icon={ClipboardList} colour="turquoise" size="sm" />
                    <h3 className="font-bold text-slate-900">Performance by Test</h3>
                  </div>
                </div>
                <div className="p-6">
                  <div className="grid sm:grid-cols-3 gap-4 mb-4">
                    {testResults.map((t) => (
                      <div key={t.test_number} className="border border-slate-200 rounded-xl p-4">
                        <div className="text-sm font-semibold text-slate-700 mb-2">Test {t.test_number}</div>
                        <div className="text-2xl font-bold text-slate-900 mb-1">{t.accuracy_pct}%</div>
                        <div className="text-xs text-slate-400 mb-3">{t.correct}/{t.total} correct</div>
                        {t.sections && Object.entries(t.sections).map(([sec, data]) => (
                          <div key={sec} className="flex items-center justify-between text-xs mb-1">
                            <span className="text-slate-500">Section {sec}</span>
                            <span className="font-medium text-slate-700">{data.accuracy_pct}% ({data.correct}/{data.total})</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Theory vs Scenarios */}
            {categories.some(c => c.scenario_total > 0) && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Icon3D icon={Stethoscope} colour="turquoise" size="sm" />
                    <h3 className="font-bold text-slate-900">Theory vs Clinical Scenarios</h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 ml-10">How you perform on straight theory versus clinical vignette questions</p>
                </div>
                <div className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-500 border-b border-slate-200">
                          <th className="pb-3 pr-4 font-semibold">Category</th>
                          <th className="pb-3 pr-4 font-semibold text-center">Theory</th>
                          <th className="pb-3 pr-4 font-semibold text-center">Scenarios</th>
                          <th className="pb-3 font-semibold text-center">Gap</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedByAccuracy
                          .filter(c => c.theory_total > 0 || c.scenario_total > 0)
                          .map((cat, i) => {
                            const gap = (cat.scenario_accuracy_pct ?? 0) - (cat.theory_accuracy_pct ?? 0);
                            return (
                              <tr key={i} className="border-b border-slate-100 last:border-0">
                                <td className="py-3 pr-4 font-medium text-slate-800 capitalize">{cat.category}</td>
                                <td className="py-3 pr-4 text-center">
                                  {cat.theory_accuracy_pct != null ? (
                                    <span className="font-semibold text-slate-800">{cat.theory_accuracy_pct}%</span>
                                  ) : <span className="text-slate-400">—</span>}
                                  {cat.theory_total > 0 && <span className="text-xs text-slate-400 ml-1">({cat.theory_total}q)</span>}
                                </td>
                                <td className="py-3 pr-4 text-center">
                                  {cat.scenario_accuracy_pct != null ? (
                                    <span className="font-semibold text-slate-800">{cat.scenario_accuracy_pct}%</span>
                                  ) : <span className="text-slate-400">—</span>}
                                  {cat.scenario_total > 0 && <span className="text-xs text-slate-400 ml-1">({cat.scenario_total}q)</span>}
                                </td>
                                <td className="py-3 text-center">
                                  {cat.theory_accuracy_pct != null && cat.scenario_accuracy_pct != null ? (
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                      Math.abs(gap) < 10 ? 'bg-slate-100 text-slate-600'
                                      : gap > 0 ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-red-100 text-red-700'
                                    }`}>
                                      {gap > 0 ? '+' : ''}{gap}%
                                    </span>
                                  ) : <span className="text-slate-400">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-400 mt-3">Negative gap = scenarios harder than theory for you. Positive gap = theory harder.</p>
                </div>
              </div>
            )}

            {/* Confidence Calibration */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Icon3D icon={Eye} colour="turquoise" size="sm" />
                  <h3 className="font-bold text-slate-900">Confidence Calibration</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1 ml-10">How well your confidence matches your actual accuracy</p>
              </div>
              <div className="p-6">
                {/* Overall confidence breakdown */}
                <div className="grid grid-cols-3 gap-4 mb-5">
                  {['high', 'medium', 'low'].map(level => {
                    const d = confidenceSummary[level] || { total: 0, correct: 0, accuracy_pct: 0 };
                    return (
                      <div key={level} className="border border-slate-200 rounded-xl p-4 text-center">
                        <div className="text-xs text-slate-500 uppercase tracking-wide mb-1 capitalize">{level} confidence</div>
                        <div className="text-2xl font-bold text-slate-900">{d.accuracy_pct}%</div>
                        <div className="text-xs text-slate-400">{d.correct}/{d.total} correct</div>
                      </div>
                    );
                  })}
                </div>

                {/* Overconfident categories */}
                {overconfidentCats.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-sm font-semibold text-red-800">Overconfident Categories</span>
                    </div>
                    <p className="text-xs text-red-700 mb-2">
                      You rated high confidence but scored below 50% — these categories may have embedded misconceptions.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {overconfidentCats.map(c => (
                        <span key={c} className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded-full font-medium capitalize">{c}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Underconfident categories */}
                {underconfidentCats.length > 0 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm font-semibold text-emerald-800">Underconfident Categories</span>
                    </div>
                    <p className="text-xs text-emerald-700 mb-2">
                      You rated low confidence but scored above 70% — you know more than you think in these areas.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {underconfidentCats.map(c => (
                        <span key={c} className="text-xs bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full font-medium capitalize">{c}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Per-category calibration table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-200">
                        <th className="pb-3 pr-4 font-semibold">Category</th>
                        <th className="pb-3 pr-4 font-semibold text-center">Accuracy</th>
                        <th className="pb-3 pr-4 font-semibold text-center">High Conf Acc</th>
                        <th className="pb-3 font-semibold text-center">Calibration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedByAccuracy.map((cat, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0">
                          <td className="py-2.5 pr-4 font-medium text-slate-800 capitalize">{cat.category}</td>
                          <td className="py-2.5 pr-4 text-center font-semibold text-slate-800">{cat.accuracy_pct}%</td>
                          <td className="py-2.5 pr-4 text-center">
                            {cat.confidence_high_accuracy_pct != null ? (
                              <span className={`font-semibold ${
                                cat.confidence_high_accuracy_pct < 50 ? 'text-red-600' : 'text-slate-800'
                              }`}>{cat.confidence_high_accuracy_pct}%</span>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="py-2.5 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              cat.calibration_gap > 5 ? 'bg-amber-100 text-amber-700'
                              : cat.calibration_gap < -5 ? 'bg-red-100 text-red-700'
                              : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {cat.calibration_gap > 0 ? 'Under' : cat.calibration_gap < -5 ? 'Over' : 'Well'}confident
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Explanation Effect Analysis */}
            {report.explanation_summary && (report.explanation_summary.with_explanation_total > 0 || report.explanation_summary.without_explanation_total > 0) && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Icon3D icon={BookOpen} colour="turquoise" size="sm" />
                    <h3 className="font-bold text-slate-900">Did Explanations Help?</h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 ml-10">Comparing accuracy on questions with explanations provided vs those without</p>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-cyan-700">{report.explanation_summary.with_explanation_accuracy_pct ?? '—'}%</div>
                      <div className="text-sm text-slate-600 mt-1">With Explanation</div>
                      <div className="text-xs text-slate-400 mt-0.5">{report.explanation_summary.with_explanation_correct}/{report.explanation_summary.with_explanation_total} correct</div>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                      <div className="text-2xl font-bold text-slate-700">{report.explanation_summary.without_explanation_accuracy_pct ?? '—'}%</div>
                      <div className="text-sm text-slate-600 mt-1">Without Explanation</div>
                      <div className="text-xs text-slate-400 mt-0.5">{report.explanation_summary.without_explanation_correct}/{report.explanation_summary.without_explanation_total} correct</div>
                    </div>
                  </div>
                  {report.explanation_summary.with_explanation_accuracy_pct != null && report.explanation_summary.without_explanation_accuracy_pct != null && (
                    <div className={`rounded-xl p-4 text-sm ${
                      report.explanation_summary.with_explanation_accuracy_pct > report.explanation_summary.without_explanation_accuracy_pct
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                        : report.explanation_summary.with_explanation_accuracy_pct < report.explanation_summary.without_explanation_accuracy_pct
                        ? 'bg-amber-50 border border-amber-200 text-amber-800'
                        : 'bg-slate-50 border border-slate-200 text-slate-700'
                    }`}>
                      {report.explanation_summary.with_explanation_accuracy_pct > report.explanation_summary.without_explanation_accuracy_pct
                        ? `Explanations appear to help — your accuracy is ${report.explanation_summary.with_explanation_accuracy_pct - report.explanation_summary.without_explanation_accuracy_pct}% higher on questions where explanations were provided. Your adaptive engine will weight explanation-backed learning more heavily in your study plan.`
                        : report.explanation_summary.with_explanation_accuracy_pct < report.explanation_summary.without_explanation_accuracy_pct
                        ? `Interestingly, you performed ${report.explanation_summary.without_explanation_accuracy_pct - report.explanation_summary.with_explanation_accuracy_pct}% better on questions without explanations provided. This suggests strong prior knowledge in those areas — your adaptive engine will focus explanations on your weaker categories.`
                        : `Your accuracy is consistent whether or not explanations are shown — your adaptive engine will use both question types equally in your study plan.`
                      }
                    </div>
                  )}
                  {/* Per-category breakdown for categories where explanation effect is significant */}
                  {categories.filter(c => c.explanation_effect != null && Math.abs(c.explanation_effect) >= 10 && c.with_explanation_total >= 3).length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Categories where effect is strongest</p>
                      <div className="space-y-2">
                        {categories
                          .filter(c => c.explanation_effect != null && Math.abs(c.explanation_effect) >= 10 && c.with_explanation_total >= 3)
                          .sort((a, b) => Math.abs(b.explanation_effect) - Math.abs(a.explanation_effect))
                          .slice(0, 4)
                          .map((cat, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                              <span className="text-slate-700 capitalize">{cat.category}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                cat.explanation_effect > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {cat.explanation_effect > 0 ? '+' : ''}{cat.explanation_effect}% with explanation
                              </span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* What appears to work — Strong areas */}
            {strongAreas.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Icon3D icon={Award} colour="green" size="sm" />
                    <h3 className="font-bold text-slate-900">What Appears to Work</h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 ml-10">Categories where you demonstrated solid knowledge</p>
                </div>
                <div className="p-6 space-y-4">
                  {strongAreas.map((cat, i) => {
                    const strength = getStrengthLabel(cat.accuracy_pct);
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-800 capitalize">{cat.category}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${strength.bg} ${strength.color}`}>
                              {strength.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-slate-400 text-xs">{cat.correct}/{cat.total}</span>
                            <span className="font-bold text-slate-800 w-10 text-right">{cat.accuracy_pct}%</span>
                          </div>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${cat.accuracy_pct}%`, background: getBarColor(cat.accuracy_pct) }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* What needs work — Weak areas */}
            {weakAreas.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Icon3D icon={AlertTriangle} colour="amber" size="sm" />
                    <h3 className="font-bold text-slate-900">Areas to Develop</h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 ml-10">Categories where focused practice will help most — the adaptive engine will prioritise these</p>
                </div>
                <div className="p-6 space-y-4">
                  {weakAreas.map((cat, i) => {
                    const strength = getStrengthLabel(cat.accuracy_pct);
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-800 capitalize">{cat.category}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${strength.bg} ${strength.color}`}>
                              {strength.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-slate-400 text-xs">{cat.correct}/{cat.total}</span>
                            <span className="font-bold text-slate-800 w-10 text-right">{cat.accuracy_pct}%</span>
                          </div>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${cat.accuracy_pct}%`, background: getBarColor(cat.accuracy_pct) }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Full category breakdown with theory/scenario */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Icon3D icon={Target} colour="turquoise" size="sm" />
                  <h3 className="font-bold text-slate-900">Full Category Breakdown</h3>
                </div>
              </div>
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-200">
                        <th className="pb-3 pr-3 font-semibold">Category</th>
                        <th className="pb-3 pr-3 font-semibold text-center">Score</th>
                        <th className="pb-3 pr-3 font-semibold text-center">Theory</th>
                        <th className="pb-3 pr-3 font-semibold text-center">Scenario</th>
                        <th className="pb-3 pr-3 font-semibold text-center">Avg Time</th>
                        <th className="pb-3 font-semibold text-center">Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedByAccuracy.map((cat, i) => {
                        const strength = getStrengthLabel(cat.accuracy_pct);
                        return (
                          <tr key={i} className="border-b border-slate-100 last:border-0">
                            <td className="py-3 pr-3 font-medium text-slate-800 capitalize">{cat.category}</td>
                            <td className="py-3 pr-3 text-center font-bold text-slate-800">{cat.accuracy_pct}%</td>
                            <td className="py-3 pr-3 text-center text-slate-600">{cat.theory_accuracy_pct != null ? `${cat.theory_accuracy_pct}%` : '—'}</td>
                            <td className="py-3 pr-3 text-center text-slate-600">{cat.scenario_accuracy_pct != null ? `${cat.scenario_accuracy_pct}%` : '—'}</td>
                            <td className="py-3 pr-3 text-center text-slate-500 text-xs">
                              {cat.avg_response_time_ms ? `${(cat.avg_response_time_ms / 1000).toFixed(1)}s` : '—'}
                            </td>
                            <td className="py-3 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${strength.bg} ${strength.color}`}>
                                {strength.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Improvement Potential — conservative, range-based, never promises */}
            {report.improvement_predictions && report.improvement_predictions.__overall__ && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center gap-2">
                    <Icon3D icon={TrendingUp} colour="turquoise" size="sm" />
                    <h3 className="font-bold text-slate-900">Your Improvement Potential</h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 ml-10">
                    How much you could improve with consistent daily practice. These are potential gains on your current level, not guarantees.
                  </p>
                  {/* Elite scaffolding status badge */}
                  {scaffoldingStatus && scaffoldingStatus.scaffolding_active && scaffoldingStatus.active_gaps?.length > 0 && (
                    <div className="mt-2 ml-10 inline-flex items-center gap-1.5 bg-cyan-50 border border-cyan-200 text-cyan-700 text-xs px-3 py-1 rounded-full">
                      <Zap className="w-3 h-3" />
                      <span className="font-medium">AI is targeting {scaffoldingStatus.active_gaps.length} weak {scaffoldingStatus.active_gaps.length === 1 ? 'area' : 'areas'} with personalised questions</span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  {/* Overall potential gains — ranges, never absolute targets */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {(() => {
                      const overall = report.improvement_predictions.__overall__;
                      const periods = [
                        { label: '1 Week', gain: overall.potential_gain_1_week },
                        { label: '1 Month', gain: overall.potential_gain_1_month },
                        { label: '3 Months', gain: overall.potential_gain_3_months },
                      ];
                      return periods.map((p, idx) => (
                        <div key={idx} className={`rounded-xl p-4 text-center border ${idx === 2 ? 'bg-cyan-50 border-cyan-200' : 'bg-slate-50 border-slate-200'}`}>
                          <div className={`text-2xl font-bold ${idx === 2 ? 'text-cyan-700' : 'text-slate-700'}`}>
                            +{p.gain}%
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">{p.label}</div>
                          <div className="text-xs text-slate-400 mt-1">potential improvement</div>
                        </div>
                      ));
                    })()}
                  </div>

                  {/* Range display */}
                  {report.improvement_predictions.__overall__.projected_range_3_months && (
                    <div className="bg-slate-50 rounded-lg p-3 mb-4 text-center">
                      <span className="text-sm text-slate-600">
                        Projected range at 3 months: <span className="font-bold text-slate-800">
                          {report.improvement_predictions.__overall__.projected_range_3_months[0]}–{report.improvement_predictions.__overall__.projected_range_3_months[1]}%
                        </span>
                      </span>
                      <span className="text-xs text-slate-400 ml-2">(currently {report.improvement_predictions.__overall__.current_accuracy}%)</span>
                    </div>
                  )}

                  {/* Effort disclaimer */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
                    <p className="text-xs text-amber-800">
                      <strong>Without consistent effort, improvement is unlikely.</strong> With effort, these gains are achievable — but there are no guarantees. We cannot predict exam difficulty, only your capacity to grow.
                    </p>
                  </div>

                  {/* Per-category sorted by priority rank (mark impact) */}
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">
                    {report.improvement_predictions.__overall__?.test_weight_pct ? 'Categories ranked by mark impact' : 'Categories ranked by growth potential'}
                  </h4>
                  <div className="space-y-3">
                    {Object.entries(report.improvement_predictions)
                      .filter(([cat]) => cat !== '__overall__')
                      .sort((a, b) => (a[1].priority_rank || 99) - (b[1].priority_rank || 99))
                      .slice(0, 8)
                      .map(([cat, pred]) => (
                        <div key={cat}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-slate-700 capitalize">{cat}</span>
                              {pred.mark_impact === 'high' && (
                                <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">High impact</span>
                              )}
                              {pred.mark_impact === 'medium' && (
                                <span className="text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full font-medium">Medium</span>
                              )}
                              {pred.test_weight_pct > 0 && (
                                <span className="text-xs text-slate-400">{pred.test_weight_pct}% of test</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                              <span className="text-slate-400">Now: {pred.current_accuracy}%</span>
                              <span className="text-emerald-600 font-bold">could gain +{pred.potential_gain_3_months}%</span>
                            </div>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden relative">
                            <div className="h-full rounded-full bg-slate-300" style={{ width: `${Math.min(pred.current_accuracy, 100)}%` }} />
                            <div
                              className="absolute top-0 h-full rounded-full bg-emerald-400 opacity-40"
                              style={{ left: `${Math.min(pred.current_accuracy, 100)}%`, width: `${Math.min(pred.potential_gain_3_months, 100 - pred.current_accuracy)}%` }}
                            />
                          </div>
                          {pred.projected_range_3_months && (
                            <div className="text-xs text-slate-400 mt-0.5">
                              Range at 3mo: {pred.projected_range_3_months[0]}–{pred.projected_range_3_months[1]}%
                            </div>
                          )}
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div
              className="rounded-2xl p-6 text-white relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #0a192f 0%, #164e63 100%)' }}
            >
              <div className="absolute top-0 right-0 w-40 h-40 opacity-10">
                <TrendingUp className="w-full h-full text-cyan-300" />
              </div>
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-cyan-400" />
                  <span className="text-cyan-400 font-semibold text-sm uppercase tracking-wide">
                    What Happens Next
                  </span>
                </div>
                <h3 className="text-lg font-bold mb-3">Your Personalised Learning Path</h3>
                <div className="space-y-2 text-sm text-slate-300 leading-relaxed">
                  <p>Based on your diagnostic results, your adaptive engine will now:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    {weakAreas.length > 0 && (
                      <li>
                        Prioritise questions in{' '}
                        <span className="text-cyan-300 font-medium">
                          {weakAreas.slice(0, 3).map(c => c.category).join(', ')}
                        </span>
                        {weakAreas.length > 3 ? ` and ${weakAreas.length - 3} more categories` : ''}
                      </li>
                    )}
                    {overconfidentCats.length > 0 && (
                      <li>
                        Target embedded misconceptions in{' '}
                        <span className="text-amber-300 font-medium">
                          {overconfidentCats.slice(0, 3).map(c => c).join(', ')}
                        </span>
                        {' '}where your confidence doesn't match your accuracy
                      </li>
                    )}
                    {categories.some(c => c.scenario_total > 0 && c.scenario_accuracy_pct != null && c.theory_accuracy_pct != null && c.scenario_accuracy_pct < c.theory_accuracy_pct - 15) && (
                      <li>Increase clinical scenario practice in categories where you struggle to apply theory</li>
                    )}
                    <li>Adjust difficulty levels to keep you in the optimal challenge zone</li>
                    <li>Use spaced repetition to reinforce concepts you found difficult</li>
                    {strongAreas.length > 0 && (
                      <li>
                        Maintain your strengths in{' '}
                        <span className="text-emerald-300 font-medium">
                          {strongAreas.slice(0, 3).map(c => c.category).join(', ')}
                        </span>
                        {' '}with periodic review questions
                      </li>
                    )}
                  </ul>
                </div>
                <button
                  onClick={() => navigate('/questions')}
                  className="mt-5 flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                >
                  Start Personalised Practice
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* ─── UPCOMING TESTS SECTION ─── */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mt-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon3D icon={Calendar} colour="turquoise" size="sm" />
                    <div>
                      <h3 className="font-bold text-slate-900">Your Upcoming Tests</h3>
                      <p className="text-xs text-slate-500">Add test dates so the adaptive engine prioritises what matters most</p>
                    </div>
                  </div>
                  {!showExamForm && (
                    <button
                      onClick={() => setShowExamForm(true)}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold transition-all"
                      style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                    >
                      <Plus className="w-4 h-4" /> Add Test Date
                    </button>
                  )}
                </div>
              </div>
              <div className="p-6">
                {/* Add Test Date Form */}
                {showExamForm && (
                  <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-200">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Test Name *</label>
                        <input
                          type="text"
                          placeholder="e.g. MBBS Year 2 Final"
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                          value={examFormData.exam_name}
                          onChange={e => setExamFormData(p => ({ ...p, exam_name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Test Date *</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                          value={examFormData.exam_date}
                          min={new Date().toISOString().slice(0, 10)}
                          onChange={e => setExamFormData(p => ({ ...p, exam_date: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Total Questions (if known)</label>
                        <input
                          type="number"
                          placeholder="e.g. 120"
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                          value={examFormData.total_questions}
                          onChange={e => setExamFormData(p => ({ ...p, total_questions: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional)</label>
                        <input
                          type="text"
                          placeholder="Any additional notes"
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                          value={examFormData.notes}
                          onChange={e => setExamFormData(p => ({ ...p, notes: e.target.value }))}
                        />
                      </div>
                    </div>
                    {/* Category multi-select */}
                    <div className="mb-3">
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Categories on this test (select all that apply)</label>
                      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 bg-white rounded-lg border border-slate-200">
                        {CATEGORIES.map(cat => {
                          const selected = examFormData.category_weights.some(cw => cw.category === cat);
                          return (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => {
                                setExamFormData(p => ({
                                  ...p,
                                  category_weights: selected
                                    ? p.category_weights.filter(cw => cw.category !== cat)
                                    : [...p.category_weights, { category: cat, weight_pct: 0, question_count: 0 }]
                                }));
                              }}
                              className={`px-2 py-1 rounded-md text-xs font-medium transition-all capitalize ${
                                selected ? 'bg-cyan-100 text-cyan-700 border border-cyan-300' : 'bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200'
                              }`}
                            >
                              {cat}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {/* Weight / question count per selected category */}
                    {examFormData.category_weights.length > 0 && (
                      <div className="mb-3">
                        <label className="text-xs font-medium text-slate-600 mb-1 block">How many questions per category? (optional — helps us prioritise)</label>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                          {examFormData.category_weights.map((cw, idx) => (
                            <div key={cw.category} className="flex items-center gap-2">
                              <span className="text-xs text-slate-600 capitalize w-28 truncate">{cw.category}</span>
                              <input
                                type="number"
                                placeholder="# Qs"
                                className="w-20 px-2 py-1 rounded border border-slate-300 text-xs"
                                value={cw.question_count || ''}
                                onChange={e => {
                                  const newWeights = [...examFormData.category_weights];
                                  newWeights[idx] = { ...cw, question_count: parseInt(e.target.value) || 0 };
                                  setExamFormData(p => ({ ...p, category_weights: newWeights }));
                                }}
                              />
                              <span className="text-xs text-slate-400">questions</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveExamDate}
                        disabled={savingExam || !examFormData.exam_name || !examFormData.exam_date}
                        className="px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                      >
                        {savingExam ? 'Saving...' : 'Save Test Date'}
                      </button>
                      <button onClick={() => setShowExamForm(false)} className="px-4 py-2 rounded-xl text-slate-500 text-sm font-medium bg-slate-100 hover:bg-slate-200">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Existing exam date cards */}
                {examDates.length > 0 ? (
                  <div className="space-y-3">
                    {examDates.map(ed => {
                      const urgency = ed.weeks_away <= 2 ? 'border-red-300 bg-red-50' : ed.weeks_away <= 4 ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white';
                      return (
                        <div key={ed.id} className={`rounded-xl p-4 border ${urgency}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-slate-800 text-sm">{ed.exam_name}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {new Date(ed.exam_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                                {' · '}
                                <span className={`font-medium ${ed.weeks_away <= 2 ? 'text-red-600' : ed.weeks_away <= 4 ? 'text-amber-600' : 'text-cyan-600'}`}>
                                  {ed.weeks_away <= 1 ? `${ed.days_away} days away` : `${Math.round(ed.weeks_away)} weeks away`}
                                </span>
                              </p>
                            </div>
                            <button onClick={() => handleDeleteExamDate(ed.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          {ed.category_weights?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {ed.category_weights.map(cw => (
                                <span key={cw.category} className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full capitalize">
                                  {cw.category}{cw.question_count > 0 ? ` ×${cw.question_count}` : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : !showExamForm && (
                  <div className="text-center py-4 text-slate-400 text-sm">
                    <Calendar className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                    No upcoming tests added yet
                  </div>
                )}
              </div>
            </div>

            {/* ─── PREVIOUS RESULTS SECTION ─── */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mt-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon3D icon={FileText} colour="turquoise" size="sm" />
                    <div>
                      <h3 className="font-bold text-slate-900">Your Previous Results</h3>
                      <p className="text-xs text-slate-500">Comparing where you were to where you are now shows your real knowledge growth</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!showResultForm && (
                      <>
                        <button
                          onClick={() => setShowResultForm(true)}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white text-xs font-semibold"
                          style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                        >
                          <Plus className="w-3.5 h-3.5" /> Add Result
                        </button>
                        <label className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-cyan-700 text-xs font-semibold bg-cyan-50 border border-cyan-200 cursor-pointer hover:bg-cyan-100 transition-colors">
                          <Upload className="w-3.5 h-3.5" /> Upload File
                          <input type="file" accept=".pdf,.xlsx,.xls" className="hidden" onChange={handleFileUpload} />
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="p-6">
                {/* Upload preview */}
                {uploadingFile && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                    <Loader2 className="w-4 h-4 animate-spin" /> Parsing your file...
                  </div>
                )}
                {uploadPreview && uploadPreview.parsed_results?.length > 0 && (
                  <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 mb-4">
                    <p className="text-sm font-medium text-cyan-800 mb-2">We found the following results — please confirm:</p>
                    {uploadPreview.parsed_results.map((pr, i) => (
                      <div key={i} className="bg-white rounded-lg p-3 mb-2 border border-cyan-100">
                        <p className="text-sm font-semibold text-slate-800">{pr.exam_name || 'Unnamed Exam'}</p>
                        <p className="text-xs text-slate-500">{pr.exam_date} · Score: {pr.overall_score_pct}%</p>
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => handleConfirmUpload(pr)} className="text-xs px-3 py-1 bg-cyan-600 text-white rounded-lg font-medium">Save</button>
                          <button onClick={() => setUploadPreview(null)} className="text-xs px-3 py-1 bg-slate-200 text-slate-600 rounded-lg font-medium">Cancel</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {uploadPreview && (!uploadPreview.parsed_results || uploadPreview.parsed_results.length === 0) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                    <p className="text-sm text-amber-800">{uploadPreview.message}</p>
                    <button onClick={() => { setUploadPreview(null); setShowResultForm(true); }} className="text-xs px-3 py-1 mt-2 bg-amber-600 text-white rounded-lg font-medium">
                      Enter Manually
                    </button>
                  </div>
                )}

                {/* Manual entry form */}
                {showResultForm && (
                  <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-200">
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Exam Name *</label>
                        <input
                          type="text" placeholder="e.g. Year 1 Final"
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                          value={resultFormData.exam_name}
                          onChange={e => setResultFormData(p => ({ ...p, exam_name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Exam Date *</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                          value={resultFormData.exam_date}
                          max={new Date().toISOString().slice(0, 10)}
                          onChange={e => setResultFormData(p => ({ ...p, exam_date: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1 block">Overall Score % *</label>
                        <input
                          type="number" placeholder="e.g. 72" min="0" max="100"
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                          value={resultFormData.overall_score_pct}
                          onChange={e => setResultFormData(p => ({ ...p, overall_score_pct: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional)</label>
                      <input
                        type="text" placeholder="Any additional info"
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm"
                        value={resultFormData.notes}
                        onChange={e => setResultFormData(p => ({ ...p, notes: e.target.value }))}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSavePastResult}
                        disabled={savingResult || !resultFormData.exam_name || !resultFormData.exam_date || !resultFormData.overall_score_pct}
                        className="px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                      >
                        {savingResult ? 'Saving...' : 'Save Result'}
                      </button>
                      <button onClick={() => setShowResultForm(false)} className="px-4 py-2 rounded-xl text-slate-500 text-sm font-medium bg-slate-100 hover:bg-slate-200">Cancel</button>
                    </div>
                  </div>
                )}

                {/* Existing past results */}
                {pastResults.length > 0 ? (
                  <div className="space-y-3">
                    {pastResults.map(pr => {
                      const scoreColor = pr.overall_score_pct >= 70 ? 'text-emerald-600' : pr.overall_score_pct >= 50 ? 'text-amber-600' : 'text-red-600';
                      return (
                        <div key={pr.id} className="rounded-xl p-4 border border-slate-200 bg-white">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-slate-800 text-sm">{pr.exam_name}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {new Date(pr.exam_date).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
                                {' · '}
                                <span className={`font-bold ${scoreColor}`}>{pr.overall_score_pct}%</span>
                                {pr.source === 'uploaded' && <span className="text-slate-400 ml-1">(uploaded)</span>}
                              </p>
                            </div>
                            <button onClick={() => handleDeletePastResult(pr.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          {pr.category_results?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {pr.category_results.map((cr, i) => (
                                <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full capitalize">
                                  {cr.category}: {cr.score_pct}%
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : !showResultForm && !uploadPreview && (
                  <div className="text-center py-4 text-slate-400 text-sm">
                    <FileText className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                    No previous results added yet
                  </div>
                )}
              </div>
            </div>

            {/* ─── AI STUDY PLAN SECTION (Professional/Elite) ─── */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mt-6" style={{ boxShadow: 'var(--shadow-card)' }}>
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon3D icon={Brain} colour="turquoise" size="sm" />
                    <div>
                      <h3 className="font-bold text-slate-900">AI Study Plan</h3>
                      <p className="text-xs text-slate-500">Personalised plan optimised to maximise your test marks</p>
                    </div>
                  </div>
                  <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-1 rounded-full font-semibold">Professional / Elite</span>
                </div>
              </div>
              <div className="p-6">
                {studyPlan ? (
                  <div>
                    {/* Strategy summary */}
                    <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 mb-4">
                      <p className="text-sm text-cyan-900 leading-relaxed">{studyPlan.strategy_summary}</p>
                      {studyPlan.target_exam_name && (
                        <p className="text-xs text-cyan-600 mt-2 font-medium">
                          Target: {studyPlan.target_exam_name} ({studyPlan.weeks_until_exam ? `${Math.round(studyPlan.weeks_until_exam)} weeks away` : studyPlan.target_exam_date})
                        </p>
                      )}
                    </div>

                    {/* Priority categories */}
                    {studyPlan.categories_to_prioritise?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Prioritise (highest mark impact)</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {studyPlan.categories_to_prioritise.map(c => (
                            <span key={c} className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded-full font-medium capitalize">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {studyPlan.categories_to_maintain?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Maintain (already strong)</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {studyPlan.categories_to_maintain.map(c => (
                            <span key={c} className="text-xs bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full font-medium capitalize">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Week-by-week plan (collapsible) */}
                    {studyPlan.weeks_plan?.length > 0 && (
                      <div className="space-y-2 mb-4">
                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Week-by-week plan</h4>
                        {studyPlan.weeks_plan.map((week, i) => (
                          <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                            <button
                              onClick={() => setExpandedPlanWeeks(p => ({ ...p, [i]: !p[i] }))}
                              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                            >
                              <span className="text-sm font-medium text-slate-700">
                                Week {week.week_number || i + 1}: {week.focus_categories?.join(', ') || 'General study'}
                              </span>
                              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedPlanWeeks[i] ? 'rotate-180' : ''}`} />
                            </button>
                            {expandedPlanWeeks[i] && (
                              <div className="px-4 pb-3 border-t border-slate-100">
                                <p className="text-xs text-slate-600 mt-2 leading-relaxed">{week.rationale}</p>
                                {week.daily_question_target > 0 && (
                                  <p className="text-xs text-cyan-600 mt-1 font-medium">Target: {week.daily_question_target} questions/day</p>
                                )}
                                {week.priority_topics?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {week.priority_topics.map((t, j) => (
                                      <span key={j} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t}</span>
                                    ))}
                                  </div>
                                )}
                                {week.focus_categories?.length > 0 && (
                                  <a
                                    href={`/questions?category=${encodeURIComponent(week.focus_categories[0].toLowerCase())}`}
                                    className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-xs font-bold rounded-lg shadow-sm hover:shadow-md transition-all"
                                  >
                                    📖 Practice This Topic
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Key recommendations */}
                    {studyPlan.key_recommendations?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Key recommendations</h4>
                        <ul className="space-y-1.5">
                          {studyPlan.key_recommendations.map((rec, i) => (
                            <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-cyan-500 flex-shrink-0 mt-0.5" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <button
                      onClick={handleGenerateStudyPlan}
                      disabled={generatingPlan}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-sm font-semibold"
                      style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                    >
                      {generatingPlan ? <><Loader2 className="w-4 h-4 animate-spin" /> Regenerating...</> : 'Regenerate Plan'}
                    </button>
                    <p className="text-xs text-slate-400 mt-2">Generated {new Date(studyPlan.generated_at).toLocaleDateString()}</p>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Brain className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm text-slate-600 font-medium mb-1">No study plan generated yet</p>
                    <p className="text-xs text-slate-400 mb-4">
                      Add your test dates and past results first, then generate a plan optimised for your marks.
                    </p>
                    <button
                      onClick={handleGenerateStudyPlan}
                      disabled={generatingPlan}
                      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-white text-sm font-semibold"
                      style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }}
                    >
                      {generatingPlan ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Brain className="w-4 h-4" /> Generate AI Study Plan</>}
                    </button>
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

      </div>
    </Layout>
  );
};

export default DiagnosticPage;
